import { useEffect, useState, type FormEvent } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Layout from '@/components/Layout'
import { fetchPost, createPost, updatePost } from '@/lib/api'

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

export default function PostForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    title: '', slug: '', excerpt: '', content: '', cover_url: '',
    author: 'PartsMarket UAE', category: 'Guides', is_active: true,
  })

  useEffect(() => {
    if (isEdit) {
      fetchPost(id!).then(p => {
        if (p) {
          setForm({
            title: p.title, slug: p.slug, excerpt: p.excerpt ?? '', content: p.content ?? '',
            cover_url: p.cover_url ?? '', author: p.author ?? 'PartsMarket UAE',
            category: p.category ?? 'Guides', is_active: p.is_active,
          })
        }
        setLoading(false)
      }).catch(() => setLoading(false))
    }
  }, [id, isEdit])

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm(f => ({ ...f, [key]: value }))
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setSaving(true)
    try {
      const payload = {
        title: form.title,
        slug: form.slug || slugify(form.title),
        excerpt: form.excerpt || null,
        content: form.content || null,
        cover_url: form.cover_url || null,
        author: form.author || 'PartsMarket UAE',
        category: form.category || 'Guides',
        published_at: new Date().toISOString(),
        is_active: form.is_active,
      }
      if (isEdit) await updatePost(id!, payload)
      else await createPost(payload)
      navigate('/blog')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save post')
    } finally {
      setSaving(false)
    }
  }

  const input = 'w-full px-3 py-2.5 rounded-xl border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent'
  const label = 'block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5'

  return (
    <Layout title={isEdit ? 'Edit Post' : 'New Post'}>
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl border border-surface-200 shadow-card p-6 md:p-8">
          {loading ? (
            <p className="text-slate-400 text-sm py-8 text-center">Loading...</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

              <div>
                <label className={label}>Title *</label>
                <input className={input} value={form.title} onChange={e => set('title', e.target.value)} required
                  placeholder="Souq Al Haraj: la guía definitiva de piezas usadas en Sharjah" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={label}>Slug</label>
                  <input className={input} value={form.slug} onChange={e => set('slug', slugify(e.target.value))} placeholder="auto-generated" />
                </div>
                <div>
                  <label className={label}>Category</label>
                  <select className={input} value={form.category} onChange={e => set('category', e.target.value)}>
                    {['Guides', 'Export', 'Business', 'News', 'Maintenance', 'Market'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className={label}>Excerpt</label>
                <textarea rows={2} className={input} value={form.excerpt} onChange={e => set('excerpt', e.target.value)}
                  placeholder="Resumen corto que aparece en la lista del blog..." />
              </div>

              <div>
                <label className={label}>Content (Markdown)</label>
                <textarea rows={14} className={`${input} font-mono text-[13px] leading-relaxed`} value={form.content}
                  onChange={e => set('content', e.target.value)}
                  placeholder={'## Título de sección\n\nEscribe el contenido del artículo en Markdown...'} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={label}>Cover image URL</label>
                  <input className={input} value={form.cover_url} onChange={e => set('cover_url', e.target.value)} placeholder="https://... or /images/..." />
                </div>
                <div>
                  <label className={label}>Author</label>
                  <input className={input} value={form.author} onChange={e => set('author', e.target.value)} />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)}
                  className="w-4 h-4 rounded border-surface-300 text-brand-600 focus:ring-brand-500" />
                Published (visible)
              </label>

              <div className="flex gap-3 pt-4 border-t border-surface-100">
                <button type="submit" disabled={saving}
                  className="bg-brand-600 text-white font-semibold text-sm px-6 py-2.5 rounded-xl hover:bg-brand-700 disabled:opacity-50 transition-colors">
                  {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Publish Post'}
                </button>
                <button type="button" onClick={() => navigate('/blog')}
                  className="bg-white border border-surface-200 text-slate-600 font-semibold text-sm px-6 py-2.5 rounded-xl hover:bg-surface-50 transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Layout>
  )
}
